import React, { useMemo, memo } from 'react';
import { Skeleton } from '@/shadcn/components/ui/skeleton';
import { SocialStatsIcon } from '@/applications/Social/components/icons/social-stats-icon';
import { SocialStatsHighlightTab } from './social-stats-highlight-tab';
import type { SocialStatsSectionProps } from '@/types/social-stats';
import { SocialStatsChartView } from './views/social-stats-chart-view';
import { transformSocialStatsToChartData } from '@/utils/social-stats';

export const SocialStatsSection: React.FC<SocialStatsSectionProps> = memo(({
  activeSocialStatTab,
  setActiveSocialStatTab,
  isLoading,
  tabs,
  tabData,
  sectionTitle = 'Social Stats',
  sectionIcon: SectionIcon = SocialStatsIcon,
  sectionIconBgColor = '#6C77DE',
}) => {
  const currentChartData = useMemo(() => {
    const data = tabData[activeSocialStatTab] || [];
    return transformSocialStatsToChartData(data);
  }, [activeSocialStatTab, tabData]);

  const activeTab = tabs.find((tab) => tab.key === activeSocialStatTab);

  if (isLoading) {
    return (
      <>
        <div className="flex items-center gap-2 mb-3">
          <Skeleton className="w-6 h-6 p-1 rounded-lg" />
          <Skeleton className="h-5 w-1/3" />
        </div>
        <div className="flex flex-col space-y-2 mt-2">
          <div className="flex space-x-2">
            {tabs.map((tab) => (
              <Skeleton key={tab.key} className="w-[200px] h-[62px] rounded-xl" />
            ))}
          </div>
          <Skeleton className="h-[200px] w-full rounded-lg" />
        </div>
      </>
    );
  }

  return (
    <>
      <h3 className="text-md font-semibold mb-3 flex items-center gap-2">
        <div
          className="w-6 h-6 p-1 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: sectionIconBgColor }}
        >
          <SectionIcon />
        </div>
        <span>{sectionTitle}</span>
      </h3>
      <div className="flex flex-col space-y-2 mt-2">
        <div className="flex space-x-2">
          {tabs.map((tab) => (
            <SocialStatsHighlightTab
              key={tab.key}
              headingText={tab.heading}
              infoText={tab.info}
              isActive={activeSocialStatTab === tab.key}
              onClick={() => setActiveSocialStatTab(tab.key)}
            />
          ))}
        </div>
        <h4 className="text-sm text-muted-foreground capitalize">
          {activeSocialStatTab}
        </h4>
        <div className="rounded-lg w-full flex items-center justify-center">
          <SocialStatsChartView
            activeTabKey={activeSocialStatTab}
            data={currentChartData}
            lineColor={activeTab?.lineColor}
          />
        </div>
      </div>
    </>
  );
});

SocialStatsSection.displayName = 'SocialStatsSection';
